import { z } from 'zod';
/**
 * Outcome of a single team re-provision run. Inlined here to avoid
 * `schemas → endpoints` import cycle (established direction is `endpoints → schemas`).
 * Keep the four members identical to `teamReprovisionResultOutcomeSchema` in
 * `endpoints/admin-teams.ts`.
 */
const lastReprovisionOutcomeSchema = z.enum(['reprovisioned', 'noop', 'skipped', 'failed']);
/**
 * Team slug grammar (spec §5.5): lowercase letter prefix, 3..41 chars,
 * lowercase letters / digits / dashes only. Single source of truth — both
 * the create-side body schema (`endpoints/teams.ts`) and the entity row
 * schema below import from here so the route-level dedupe regex never
 * drifts from the persisted shape.
 */
export const teamSlugSchema = z
    .string()
    .regex(/^[a-z][a-z0-9-]{2,40}$/, 'team name must match ^[a-z][a-z0-9-]{2,40}$ (spec §5.5)');
/**
 * Maps a team slug to the PascalCase suffix used in the ADO repo name. The DB
 * slug is kebab-lowercase for DNS / URL safety; the ADO repo name follows
 * the .NET reverse-DNS namespace convention used by the parent
 * `Cotality.Marketplace` and `Cotality.Marketplace.TeamTemplate` repos
 * (productionization spec §4.3, §5.5). Single source of truth — the UI
 * preview chip and the provision-team worker both consume this.
 *
 * Example: `platform-eng` → `PlatformEng` → `Cotality.Marketplace.PlatformEng`
 */
export function slugToAdoRepoSuffix(slug) {
    return slug
        .split('-')
        .filter((part) => part.length > 0)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join('');
}
/**
 * Team — display name + ADO repo pointer + source link. Spec §4.1, §4.3.
 * `name` is the kebab-case slug used by team-management and ADO repo
 * provisioning (spec §5.5: "validate name (slug format, unique)"). 1:1 with
 * a provisioned `plugin_sources` row whose ADO repo suffix is derived from
 * this slug.
 */
export const teamSchema = z.object({
    id: z.string().uuid(),
    // Strict kebab-case — shared `teamSlugSchema` keeps the entity row and
    // `createTeamBodySchema.name` aligned without regex duplication.
    name: teamSlugSchema,
    displayName: z.string().nullable(),
    description: z.string().nullable(),
    adoProject: z.string().default('Cotality.Labs'),
    adoRepoName: z.string().min(1),
    // ADO Team id of the `<DisplayName> Admins` Team (seeded from team_admin
    // roster, holds EditPolicies). Read-only — provisioning is the sole writer.
    // Null until provisioned. (`teams.ado_admin_team_id`)
    adoAdminTeamId: z.string().uuid().nullable(),
    // ADO Team id of the `<DisplayName> Reviewers` Team (starts empty, required
    // reviewer on main). Read-only. Null until provisioned. (`teams.ado_reviewer_team_id`)
    adoReviewerTeamId: z.string().uuid().nullable(),
    // Latest team_reprovision_results.created_at for this team (NTH #2). Null = never.
    lastReprovisionedAt: z.string().datetime().nullable(),
    // Latest team_reprovision_results.outcome for this team. Null = never run.
    lastReprovisionOutcome: lastReprovisionOutcomeSchema.nullable(),
    sourceId: z.string().uuid().nullable(),
    // Projected from `plugin_sources.url` via `source_id`; null when the team
    // has no source or the URL is still the 'pending' provisioning placeholder.
    repoUrl: z.string().url().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    deletedAt: z.string().datetime().nullable(),
});
/** Lightweight ref emitted inside plugin / submission payloads. */
export const teamRefSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    displayName: z.string().nullable(),
});
//# sourceMappingURL=team.js.map